import {
  getInvalidEmailValidationMSG,
  getStringValidationMSG,
} from "@/domain/validations/common.validations";
import type {
  AuthTokens,
  RegistrationData,
  RegistrationDataForm,
  UserCredentials,
} from "@/domain/value-objects/auth";
import { z } from "zod";
import { UserValidations } from "@/domain/entities-new/user/user.validation.messages";
import {
  CreateUserPreferences,
  ExtraPreferences,
  PasswordResetData,
  UpdateUser,
  UpdateUserPreferences,
  User,
  UserPreferences,
} from "@/domain/entities-new/user/user.entity";
import {
  zOptionalValidatedString,
  zValidatedBoolean,
  zValidatedEmptyString,
  zValidatedString,
} from "@/lib/zod/helpers";

/*
 * - Auth Schemas
 */

const LoginSchema = z.object({
  email: z
    .string({ message: getStringValidationMSG("correo") })
    .email({ message: getInvalidEmailValidationMSG("correo") }),
  password: z
    .string({ message: getStringValidationMSG("contraseña") })
    .min(UserValidations.UserPassword.Min.value, {
      message: UserValidations.UserPassword.Min.message,
    }),
});
LoginSchema._output satisfies UserCredentials;

const RegistrationSchema = LoginSchema.extend({
  password_confirmation: z
    .string({ message: getStringValidationMSG("contraseña") })
    .min(UserValidations.UserPassword.Min.value, {
      message: UserValidations.UserPassword.Min.message,
    }),
  first_name: z
    .string({ message: getStringValidationMSG("nombre") })
    .nonempty({ message: getStringValidationMSG("nombre") }),
  last_name: z
    .string({ message: getStringValidationMSG("apellido") })
    .nonempty({
      message: getStringValidationMSG("apellido"),
    }),
  country: z.string({ message: getStringValidationMSG("país") }),
}).refine((data) => data.password === data.password_confirmation, {
  path: ["password_confirmation"],
  message: "Las contraseñas no coinciden",
});
RegistrationSchema._output satisfies RegistrationDataForm;

const RegistrationAPIRequestSchema = RegistrationSchema.sourceType().omit({
  password_confirmation: true,
});
RegistrationAPIRequestSchema._output satisfies RegistrationData;

const AuthTokensSchema = z.object({
  access_token: z.string({
    message: getStringValidationMSG("Token de acceso"),
  }),
  refresh_token: z.string({
    message: getStringValidationMSG("Token de renovación"),
  }),
});
AuthTokensSchema._output satisfies AuthTokens;

const PasswordResetTriggerSchema = z.object({
  email: z
    .string({ message: getStringValidationMSG("correo") })
    .email({ message: getInvalidEmailValidationMSG("correo") }),
});

const PasswordResetSchema = z.object({
  email: z
    .string({ message: getStringValidationMSG("correo") })
    .email({ message: getInvalidEmailValidationMSG("correo") }),
  password: z
    .string({ message: getStringValidationMSG("contraseña") })
    .min(UserValidations.UserPassword.Min.value, {
      message: UserValidations.UserPassword.Min.message,
    }),
  token: z.string({ message: getStringValidationMSG("token") }),
});
PasswordResetSchema._output satisfies PasswordResetData;

/*
 * - User Schemas
 */

const UserSchema = z.object({
  id: zValidatedString("ID de usuario"),
  first_name: zValidatedString("nombre"),
  last_name: zValidatedString("apellido"),
  country: zValidatedString("país"),
  profile_picture_url: zValidatedEmptyString("URL de imagen de perfil"),
  trainer_id: zValidatedString("ID de entrenador"),
  email: zValidatedString("correo"),
  role: zValidatedString("rol"),
  last_login: zValidatedString("último inicio de sesión"),
  enabled: zValidatedBoolean("cuenta habilitada"),
});
UserSchema._output satisfies User;

const UpdateUserSchema = z.object({
  first_name: zOptionalValidatedString("nombre"),
  last_name: zOptionalValidatedString("apellido"),
  country: zOptionalValidatedString("país"),
  profile_picture_url: zOptionalValidatedString("URL de imagen de perfil"),
});
UpdateUserSchema._output satisfies UpdateUser;

/*
 * - User Preferences Schemas
 */

const ExtraPreferencesSchema = z.object({
  onboarding_tour_watched: zValidatedBoolean("tour de introducción").default(
    false
  ),
  pwa_installation_shown: zValidatedBoolean("popup de instalación PWA").default(
    false
  ),
});
ExtraPreferencesSchema._output satisfies ExtraPreferences;

const createUserPreferencesSchema = z.object({
  metric_system: zValidatedString("sistema métrico"),
  theme: zValidatedString("tema"),
  language: zValidatedString("idioma"),
  timezone: zValidatedString("zona horaria"),
  extra: zValidatedEmptyString("preferencias extra"),
});
createUserPreferencesSchema._output satisfies CreateUserPreferences;

const UserPreferencesSchema = createUserPreferencesSchema.extend({
  id: zValidatedString("ID de preferencias de usuario"),
  user_id: zValidatedString("ID de usuario"),
});
UserPreferencesSchema._output satisfies UserPreferences;

const UpdateUserPreferencesSchema = z.object({
  metric_system: zOptionalValidatedString("sistema métrico"),
  theme: zOptionalValidatedString("tema"),
  language: zOptionalValidatedString("idioma"),
  timezone: zOptionalValidatedString("zona horaria"),
});
UpdateUserPreferencesSchema._output satisfies UpdateUserPreferences;

/*
 * - Exports
 */

export const AuthSchemas = {
  Login: LoginSchema,
  Registration: {
    Form: RegistrationSchema,
    APIRequest: RegistrationAPIRequestSchema,
  },
  AuthTokens: AuthTokensSchema,
  PasswordReset: {
    Trigger: PasswordResetTriggerSchema,
    Reset: PasswordResetSchema,
  },
};

export const UserSchemas = {
  User: UserSchema,
  Update: UpdateUserSchema,
};

export const UserPreferencesSchemas = {
  UserPreferences: UserPreferencesSchema,
  Update: UpdateUserPreferencesSchema,
  Create: createUserPreferencesSchema,
  ExtraPreferencesSchema: ExtraPreferencesSchema,
};
